import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import {useParams} from "react-router-dom";
import useEmbeddedShows from "view/EmbeddedShows/helpers/useEmbeddedShows";
import filterReducer, {
  ActionType,
  getSortLabel,
  initialState,
  SortOption,
} from "view/EmbeddedShows/helpers/filterSortReducer";
import filterShows from "view/EmbeddedShows/helpers/filterShows";
import {debounce} from "lodash";
import StepWrapper from "view/components/StepWrapper";
import {
  Box,
  CircularProgress,
  makeStyles,
  Typography,
} from "@material-ui/core/";
import FlexBox from "view/components/Layout/FlexBox";
import ShowCard from "view/apps/Organiser/pages/Settings/EmbeddedShowList/components/ShowCard";
import SearchBar from "view/EmbeddedShows/components/SearchBar";
import CircleIconWithDot from "view/images/CircleIconWithDot";

const EmbeddedShows = () => {
  const classes = useStyles();
  const {token} = useParams();
  const [{shows, buttonColor, buttonTextColor}, isLoading] = useEmbeddedShows(
    token
  );
  const [filterState, dispatch] = useReducer(filterReducer, initialState);
  const filteredShows = useMemo(() => filterShows(shows, filterState), [
    filterState,
    shows,
  ]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const debouncedSearchQuery = useMemo(
    () =>
      debounce((searchValue: string) => {
        dispatch({type: ActionType.SET_SEARCH_QUERY, payload: searchValue});
      }, 500),
    []
  );
  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      debouncedSearchQuery(e.target.value);
    },
    [debouncedSearchQuery]
  );
  const handleSortChange = (sortValue: SortOption["value"]) => () => {
    dispatch({type: ActionType.SET_SORT_SETTING, payload: sortValue});
    handleClose();
  };

  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({type: "EMBEDDED_SHOWS_HEIGHT", height}, "*");
    };

    sendHeight();
    const resizeObserver = new ResizeObserver(sendHeight);

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (isLoading && !shows.length) {
    return <CircularProgress />;
  }
  if (!isLoading && !shows.length) {
    return <Typography>The embedded show list is empty</Typography>;
  }
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box className={classes.root}>
      <StepWrapper index={0} isComplete CustomIconComponent={CircleIconWithDot}>
        <SearchBar
          onSearch={handleSearchChange}
          onClick={handleClick}
          sortLabel={getSortLabel(filterState)}
          anchorEl={anchorEl}
          onClose={handleClose}
          onSortChange={handleSortChange}
        />
        <FlexBox flexWrap="wrap" gap={3}>
          {filteredShows.length ? (
            filteredShows.map((show) => (
              <Box width={230} height={300} key={show.id}>
                <ShowCard
                  show={show}
                  isSolidCard
                  buttonTextColor={buttonTextColor}
                  buttonColor={buttonColor}
                />
              </Box>
            ))
          ) : (
            <Typography>No shows found for your search.</Typography>
          )}
        </FlexBox>
      </StepWrapper>
    </Box>
  );
};

const useStyles = makeStyles(({spacing}) => ({
  "@global": {
    "body, #main": {
      backgroundColor: "transparent !important",
    },
  },
  root: {
    padding: spacing(3),
    width: "100%",
    height: "auto",
    backgroundColor: "transparent !important",
  },
}));

export default EmbeddedShows;
